import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@components/shadcn-ui/tabs";
import { useEffect, useState } from "react";
import { getPresignedUrlFromApplicationId } from "../service/uploadFileService";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@components/shadcn-ui/table";
import { Button } from "@components/shadcn-ui/button";
import { Download, Eye } from "lucide-react";
import { FilePreview } from "@components/ui/FilePreview";
import Snipper from "@components/ui/Snipper";
import { Badge } from "@components/shadcn-ui/badge";

const sections = [
  "ALL FILES",
  "APPLICATION",
  "PAN",
  "AADHAR",
  "BANK",
  "PROJECT",
  "INVOICE",
  "OTHERS",
];

interface Document {
  presignedUrl: string;
  objectKey: string;
}

type DocumentsType = Record<string, Document[]>;

export function FileManager({ applicationId }: { applicationId: string }) {
  const [documents, setDocuments] = useState<DocumentsType>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      setError(null);

      try {
        const { documents } = await getPresignedUrlFromApplicationId(
          applicationId
        );

        setDocuments(documents);
      } catch (err) {
        setError("Failed to fetch files.");
      } finally {
        setLoading(false);
      }
    };

    if (applicationId) {
      fetchFiles();
    }
  }, [applicationId]);

  const allFiles = Object.values(documents).flat();

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 w-full">
        <Snipper />
        <p>Fetching documents...</p>
      </div>
    );
  }

  if (error) {
    return <p>{error}</p>;
  }

  return (
    <>
      <Tabs defaultValue="ALL FILES" className="w-full mt-0">
        <TabsList>
          {sections.map((section) => (
            <TabsTrigger key={section} value={section}>
              {section}
              {/* Create a component */}
              {(section === "ALL FILES"
                ? allFiles?.length
                : documents[section]?.length) > 0 && (
                <Badge className="py-0 px-2 ml-2">
                  {section === "ALL FILES"
                    ? allFiles?.length || 0
                    : documents[section]?.length || 0}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {sections.map((section) => {
          const documents_ =
            section === "ALL FILES" ? allFiles : documents[section] || [];
          return (
            <TabsContent key={section} value={section}>
              <Table>
                <TableHeader className="bg-blue-200">
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>File Image</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <>
                    {!(documents_.length > 0) ? (
                      <div className="m-3 text-center text-center text-gray-500 p-4">
                        You have not uploaded any documents in this section.
                      </div>
                    ) : (
                      <>
                        {documents_?.map((doc, index) => (
                          <TableRow
                            key={index}
                            className="bg-white cursor-pointer"
                          >
                            <TableCell className="py-1">
                              <img
                                src={doc.presignedUrl}
                                alt=""
                                className="max-h-[50px] w-[50px]"
                              />
                            </TableCell>
                            <TableCell className="py-1">
                              {doc.objectKey.split("/").pop()}
                            </TableCell>
                            <TableCell className="py-1 flex gap-1">
                              <Button>
                                <a
                                  href={doc.presignedUrl || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Download />
                                </a>
                              </Button>
                              <Button
                                onClick={() => {
                                  setShowImageModal(true);
                                  setModalImage(doc.presignedUrl);
                                }}
                              >
                                <Eye />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </>
                </TableBody>
              </Table>
            </TabsContent>
          );
        })}
      </Tabs>
      <FilePreview
        modalImage={modalImage}
        closeModal={() => {
          setShowImageModal(false);
          setModalImage(null);
        }}
        showImageModal={showImageModal}
        setShowImageModal={setShowImageModal}
      />
    </>
  );
}
